import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Calculator, Copy, RefreshCw, AlertCircle, CheckCircle2, Info } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FaqSection } from "@/components/seo/FaqSection";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { computeCheckDigit, type GtinKind } from "@/lib/gtin";
import { usePrefillValue } from "@/lib/usePrefillValue";

const PATH = "/check-digit-calculator";
const TITLE = "Barcode Check Digit Calculator — UPC, EAN & GTIN";
const DESC =
  "Free barcode check digit calculator. Compute the correct UPC-A, EAN-13, EAN-8 and GTIN-14 check digit from the body digits — runs entirely in your browser.";

type Kind = "auto" | GtinKind;

const BODY_LENGTH: Record<GtinKind, number> = {
  "GTIN-8": 7,
  "GTIN-12": 11,
  "GTIN-13": 12,
  "GTIN-14": 13,
};

const KIND_LABEL: Record<GtinKind, string> = {
  "GTIN-8": "GTIN-8 / EAN-8",
  "GTIN-12": "GTIN-12 / UPC-A",
  "GTIN-13": "GTIN-13 / EAN-13",
  "GTIN-14": "GTIN-14 / ITF-14",
};

const EXAMPLES: Array<{ body: string; kind: GtinKind; note: string }> = [
  { body: "7351353", kind: "GTIN-8", note: "EAN-8 sample" },
  { body: "03600029145", kind: "GTIN-12", note: "UPC-A sample" },
  { body: "501234567890", kind: "GTIN-13", note: "EAN-13 sample" },
  { body: "1061414100041", kind: "GTIN-14", note: "ITF-14 carton" },
];

const FAQ = [
  {
    q: "How does a barcode check digit calculator work?",
    a: "It applies the GS1 mod-10 algorithm: multiply each body digit by 3 or 1 in an alternating pattern (counted from the right of the body), sum the results, then pick the digit that brings the total up to the next multiple of 10. The result is appended to the body to form the complete barcode.",
  },
  {
    q: "Which Formats Does This Calculator Support?",
    a: "UPC-A (GTIN-12), EAN-13 (GTIN-13), EAN-8 (GTIN-8) and ITF-14 (GTIN-14). All four share the same mod-10 algorithm — only the body length differs.",
  },
  {
    q: "What Do I Paste — the Full Barcode or Just the Body?",
    a: "Just the body, without the trailing check digit. For UPC-A that's 11 digits, for EAN-13 it's 12, for EAN-8 it's 7, for GTIN-14 it's 13. In Auto-detect mode the calculator picks the format from the length you entered.",
  },
  {
    q: "Can I use this as an EAN-13 check digit calculator?",
    a: "Yes. Pick GTIN-13 / EAN-13 (or use Auto-detect) and paste the first 12 digits. The 13th digit shown in the result is the EAN-13 check digit.",
  },
  {
    q: "Does It Work for UPC-E?",
    a: "UPC-E codes are derived from a UPC-A. Calculate the UPC-A check digit here using the 11-digit body; the UPC-E carries that same check digit. The UPC Lookup tool expands a UPC-E back to its UPC-A if you need the body.",
  },
  {
    q: "Are My Numbers Sent to a Server?",
    a: "No. The calculation runs entirely in your browser. Nothing is uploaded, logged or stored.",
  },
  {
    q: "What Digit Is the Check Digit?",
    a: "It is the last digit of the number: the 12th digit of a UPC-A, the 13th of an EAN-13, the 8th of an EAN-8 and the 14th of a GTIN-14. It is printed at the right end of the human-readable line and encoded in the bars like the other digits. This calculator returns it from the digits that come before it.",
  },
  {
    q: "Can I Calculate an SSCC Check Digit?",
    a: "Not with this calculator, which only accepts GTIN body lengths and rejects the 17 digits of an SSCC body. The SSCC uses the same GS1 mod-10 method, so the by-hand steps on this page work: weight the 17 digits 3 and 1 from the right, starting with 3, and close the sum to the next multiple of 10. The result is the 18th digit.",
  },
  {
    q: "Is There a Check Digit Calculator for Excel?",
    a: "This tool has no Excel add-in or export, but a spreadsheet formula can do the same math. Keep the body as text so leading zeros stay, pull out each digit with MID, weight it 3 or 1 from the right, add the products with SUMPRODUCT, and finish with MOD(10 - MOD(sum, 10), 10). Test the formula against a few results from this page before using it on a whole column.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Lookup & Validators", path: "/lookup-validators" },
  { name: "Check Digit Calculator", path: PATH },
];

function howToJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Calculate a barcode check digit (UPC / EAN / GTIN)",
    description:
      "Compute the GS1 mod-10 check digit for a UPC-A, EAN-13, EAN-8 or GTIN-14 barcode body.",
    step: [
      {
        "@type": "HowToStep",
        name: "Pick the format",
        text: "Select GTIN-8, GTIN-12 (UPC-A), GTIN-13 (EAN-13) or GTIN-14, or leave Auto-detect on.",
      },
      {
        "@type": "HowToStep",
        name: "Enter the body digits",
        text: "Paste the barcode body without the trailing check digit.",
      },
      {
        "@type": "HowToStep",
        name: "Multiply alternating digits",
        text: "From the right of the body, multiply odd-position digits by 3 and even-position digits by 1.",
      },
      {
        "@type": "HowToStep",
        name: "Sum the products",
        text: "Add every weighted digit together.",
      },
      {
        "@type": "HowToStep",
        name: "Round up to a multiple of 10",
        text: "The check digit is whatever value brings the sum up to the next multiple of 10. Append it to the body to get the full barcode.",
      },
    ],
  };
}

export const Route = createFileRoute("/check-digit-calculator")({
  component: CheckDigitCalculatorPage,
  head: () => {
    const base = pageHead({ title: TITLE, description: DESC, path: PATH });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Check Digit Calculator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(howToJsonLd()),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

type Calc =
  | { state: "idle" }
  | { state: "error"; message: string; cleaned: string }
  | {
      state: "ok";
      kind: GtinKind;
      body: string;
      checkDigit: number;
      fullCode: string;
      breakdown: Array<{ digit: number; weight: 1 | 3; product: number; position: number }>;
      sum: number;
      nextMultiple: number;
    };

function detectKind(bodyLen: number): GtinKind | null {
  for (const k of Object.keys(BODY_LENGTH) as GtinKind[]) {
    if (BODY_LENGTH[k] === bodyLen) return k;
  }
  return null;
}

function calculate(rawInput: string, kindChoice: Kind): Calc {
  const cleaned = rawInput.replace(/[\s\-_.]/g, "");
  if (!cleaned) return { state: "idle" };
  if (!/^\d+$/.test(cleaned)) {
    return {
      state: "error",
      cleaned,
      message:
        "Only digits are allowed. Spaces, dashes, underscores and dots are stripped automatically.",
    };
  }
  let kind: GtinKind | null;
  if (kindChoice === "auto") {
    kind = detectKind(cleaned.length);
    if (!kind) {
      return {
        state: "error",
        cleaned,
        message: `Body must be 7, 11, 12 or 13 digits long for GTIN-8 / 12 / 13 / 14. You entered ${cleaned.length}.`,
      };
    }
  } else {
    kind = kindChoice;
    if (cleaned.length !== BODY_LENGTH[kind]) {
      return {
        state: "error",
        cleaned,
        message: `${kind} expects exactly ${BODY_LENGTH[kind]} body digits (excluding the check digit). You entered ${cleaned.length}.`,
      };
    }
  }

  const check = computeCheckDigit(cleaned);

  // Build the per-digit breakdown matching computeCheckDigit:
  // walking the body from the right, position 1 weight 3, position 2 weight 1, …
  const breakdown: Array<{ digit: number; weight: 1 | 3; product: number; position: number }> = [];
  let sum = 0;
  for (let i = 0; i < cleaned.length; i++) {
    const position = i + 1; // 1-indexed from the right
    const digit = Number(cleaned[cleaned.length - 1 - i]);
    const weight: 1 | 3 = position % 2 === 1 ? 3 : 1;
    const product = digit * weight;
    sum += product;
    breakdown.push({ digit, weight, product, position });
  }
  // Display order: left → right of the body, so reverse.
  breakdown.reverse();
  const nextMultiple = Math.ceil(sum / 10) * 10;

  return {
    state: "ok",
    kind,
    body: cleaned,
    checkDigit: check,
    fullCode: cleaned + String(check),
    breakdown,
    sum,
    nextMultiple,
  };
}

function CheckDigitCalculatorPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Barcode Check Digit Calculator"
      description="Enter the body of a UPC, EAN or GTIN and we'll compute the correct check digit — with a full step-by-step breakdown. Auto-detect picks GTIN-8, GTIN-12, GTIN-13 or GTIN-14 from the body length (7, 11, 12 or 13 digits), and the complete code can be copied in one click."
      crumbs={CRUMBS}
      content={
        <>
          <ExamplesSection />
          <ToolSeoSections
            aboutHeading="About this barcode check digit calculator"
            about={
              <>
                <p>
                  This barcode check digit calculator computes the final digit of a UPC-A, EAN-13, EAN-8
                  or GTIN-14 barcode using the GS1 mod-10 algorithm. Paste the body digits — everything
                  except the trailing check digit — and the tool returns the missing digit, the complete
                  code, and a step-by-step breakdown of how the result was reached.
                </p>
                <p>
                  Use it whenever you need to calculate a GTIN check digit by hand, verify what a
                  printed barcode <em>should</em> end with, or generate a fresh code from a body issued
                  by your numbering authority.
                </p>
              </>
            }
            whenToUse={[
              "Generating a new EAN-13 from an issued 12-digit body before sending it to a label printer.",
              "Calculating a UPC check digit when a supplier sent you only the first 11 digits.",
              "Building a GTIN-14 carton code on top of an inner GTIN-13.",
              "Recomputing a check digit after correcting a typo in the body.",
              "Teaching a teammate how the GS1 mod-10 algorithm actually works.",
            ]}
            howItWorks={[
              {
                title: "Pick a format",
                desc: "Choose GTIN-8/12/13/14, or leave Auto-detect on and we'll pick by length.",
              },
              {
                title: "Paste the body",
                desc: "Enter the digits without the check digit. Spaces, dashes and dots are stripped.",
              },
              {
                title: "We weight each digit",
                desc: "Walking from right to left, odd positions get ×3, even positions get ×1.",
              },
              {
                title: "We sum and round up",
                desc: "The weighted sum is rounded up to the next multiple of 10.",
              },
              {
                title: "You get the check digit",
                desc: "The difference between the rounded value and the sum is your check digit. Copy the full code in one click.",
              },
            ]}
            whatToAvoid={[
              "Pasting the full barcode including the existing check digit — use the GTIN Validator for that.",
              "Mixing UPC-A and UPC-E lengths — UPC-E needs to be expanded first.",
              "Trusting hand-computed digits without re-verifying; one digit off cascades through the whole code.",
              "Treating the calculator as a product database — it doesn't look up names or prices.",
              "Generating a GTIN body yourself; bodies must come from a GS1-assigned prefix.",
            ]}
            tips={[
              "If Auto-detect picks the wrong format, switch the dropdown to the format you intend.",
              "Click an example below to see the full step-by-step breakdown instantly.",
              "When the sum is already a multiple of 10, the check digit is 0 — not 10.",
              "For ITF-14, the leading 'indicator digit' (1–8) is part of the body and is weighted ×3.",
              "Pair this with the GTIN Validator to confirm an existing code after you've calculated the check digit.",
            ]}
          />
          <h2>GS1 Check Digit Calculator</h2>
          <p>This check digit calculator uses the GS1 standard mod-10 method, the same one GS1 specifies for GTINs, GLNs and SSCCs.</p>
          <p>It accepts GTIN bodies of 7, 11, 12 or 13 digits. A GLN body has 12 digits like a GTIN-13 body, so GTIN-13 mode gives its check digit, while a 17-digit SSCC body is rejected.</p>
          <p>Used as a UPC check digit calculator it takes 11 digits, and as an EAN check digit calculator 12 digits for EAN-13 or 7 for EAN-8. For a code that already ends in its check digit, use the <Link to="/gtin-validator">GTIN validator</Link> instead.</p>
          <h3>GTIN Calculator for GTIN-8 to GTIN-14</h3>
          <p>As a GTIN check digit calculator it also builds a case-level GTIN-14 from an item's GTIN:</p>
          <ol>
            <li>Drop the item's check digit, and pad a UPC-A body with a leading 0.</li>
            <li>Put an indicator digit from 1 to 8 in front.</li>
            <li>Enter the 13-digit body in GTIN-14 mode.</li>
          </ol>
          <p>The check digit always changes, because the indicator adds three times its value to the weighted sum. The UPC-A 614141000418 with indicator 1 becomes the GTIN-14 10614141000415.</p>
          <h2>How to Calculate a Check Digit by Hand</h2>
          <p>Weight the body digits 3 and 1 alternately, starting with 3 on the right, add the products, and subtract the total from the next multiple of 10. For the EAN-8 body 7351353:</p>
          <ol>
            <li>Weights from the left: 3, 1, 3, 1, 3, 1, 3.</li>
            <li>Products: 21, 3, 15, 1, 9, 5, 9, for a sum of 63.</li>
            <li>70 − 63 = 7, so the full EAN-8 is 73513537.</li>
          </ol>
          <p>Counting from the left, the first digit of a 7-, 11- or 13-digit body is weighted 3, but the first digit of a 12-digit body is weighted 1. To check a finished code, start with 1 on the check digit; the total must end in 0. The <Link to="/barcode-guides/check-digits-explained">check digits explained guide</Link> covers why this catches typos.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <CalculatorApp />
      <SupportedTypes />
    </ToolLayout>
  );
}

function CalculatorApp() {
  const [value, setValue] = useState("");
  const [kind, setKind] = useState<Kind>("auto");
  usePrefillValue((v) => setValue(v));

  const calc = useMemo(() => calculate(value, kind), [value, kind]);

  const reset = () => setValue("");

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col gap-3">
            <label htmlFor="cdc-input" className="text-sm font-medium">
              Body digits (without check digit)
            </label>
            <Input
              id="cdc-input"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 03600029145"
              maxLength={20}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-invalid={calc.state === "error"}
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label htmlFor="cdc-kind" className="text-sm text-muted-foreground">
                Format
              </label>
              <Select value={kind} onValueChange={(v) => setKind(v as Kind)}>
                <SelectTrigger id="cdc-kind" className="w-full sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect (by length)</SelectItem>
                  {(Object.keys(KIND_LABEL) as GtinKind[]).map((k) => (
                    <SelectItem key={k} value={k}>
                      {KIND_LABEL[k]} — {BODY_LENGTH[k]} body digits
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex.body}
                  type="button"
                  onClick={() => {
                    setValue(ex.body);
                    setKind(ex.kind);
                  }}
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  aria-label={`${ex.note} — ${ex.body}`}
                >
                  {ex.body}
                </button>
              ))}
            </div>
            <div>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              All math happens in your browser. Nothing is uploaded.
            </p>
          </div>
        </CardContent>
      </Card>

      <ResultPanel calc={calc} />
    </div>
  );
}

function ResultPanel({ calc }: { calc: Calc }) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Result
        </p>
        <div className="mt-4 flex-1">
          {calc.state === "idle" && (
            <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
              <Calculator className="h-7 w-7 text-muted-foreground" aria-hidden />
              <p className="max-w-sm text-sm text-muted-foreground">
                Enter the body digits and the check digit will appear here, with the full
                weighted-sum breakdown.
              </p>
            </div>
          )}

          {calc.state === "error" && (
            <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Can't calculate yet</span>
              </div>
              <p className="text-sm text-muted-foreground">{calc.message}</p>
              {calc.cleaned && (
                <div className="rounded-md border border-border bg-background p-3 font-mono text-xs">
                  {calc.cleaned}{" "}
                  <span className="text-muted-foreground">
                    ({calc.cleaned.length} digit{calc.cleaned.length === 1 ? "" : "s"})
                  </span>
                </div>
              )}
            </div>
          )}

          {calc.state === "ok" && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">{calc.kind} check digit calculated</span>
                <Badge variant="outline" className="text-xs">
                  Check digit {calc.checkDigit}
                </Badge>
              </div>
              <Row label="Check digit" value={String(calc.checkDigit)} mono onCopy={copy} />
              <Row label="Full code" value={calc.fullCode} mono onCopy={copy} />
              <Row label="Body" value={calc.body} mono />
              <Steps calc={calc} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Steps({ calc }: { calc: Extract<Calc, { state: "ok" }> }) {
  return (
    <div className="rounded-md border border-border bg-background">
      <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Step-by-step
      </div>
      <div className="overflow-x-auto px-3 py-3">
        <table className="w-full min-w-[18rem] text-xs">
          <thead className="text-muted-foreground">
            <tr className="text-left">
              <th className="py-1 pr-3">Digit</th>
              <th className="py-1 pr-3">Weight</th>
              <th className="py-1">Product</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {calc.breakdown.map((row, i) => (
              <tr key={i} className="border-t border-border/60">
                <td className="py-1 pr-3">{row.digit}</td>
                <td className="py-1 pr-3">× {row.weight}</td>
                <td className="py-1">{row.product}</td>
              </tr>
            ))}
            <tr className="border-t border-border">
              <td colSpan={2} className="py-1 pr-3 font-sans text-muted-foreground">
                Sum of products
              </td>
              <td className="py-1">{calc.sum}</td>
            </tr>
            <tr>
              <td colSpan={2} className="py-1 pr-3 font-sans text-muted-foreground">
                Next multiple of 10
              </td>
              <td className="py-1">{calc.nextMultiple}</td>
            </tr>
            <tr className="border-t border-border">
              <td colSpan={2} className="py-1 pr-3 font-sans text-muted-foreground">
                {calc.nextMultiple} − {calc.sum} =
              </td>
              <td className="py-1 font-semibold text-foreground">{calc.checkDigit}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
        <Info className="mr-1 inline h-3.5 w-3.5 text-primary" />
        Walking right-to-left through the body, odd positions are multiplied by 3, even by 1. The
        check digit closes the sum to the next multiple of 10.
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy?: (s: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-0.5 break-all text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      {onCopy && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onCopy(value)}
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function SupportedTypes() {
  const types = [
    {
      kind: "GTIN-8 / EAN-8",
      body: "7 digits",
      note: "Small retail packages where space is tight.",
    },
    {
      kind: "GTIN-12 / UPC-A",
      body: "11 digits",
      note: "Standard US and Canadian retail barcode.",
    },
    {
      kind: "GTIN-13 / EAN-13",
      body: "12 digits",
      note: "Global retail standard outside North America.",
    },
    {
      kind: "GTIN-14 / ITF-14",
      body: "13 digits",
      note: "Cases, cartons and pallets in logistics.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Supported Barcode Types</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        The calculator handles every GTIN length that uses the GS1 mod-10 algorithm.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {types.map((t) => (
          <div key={t.kind} className="rounded-lg border border-border bg-background p-4">
            <div className="text-sm font-semibold">{t.kind}</div>
            <div className="mt-1 text-xs text-muted-foreground">Body: {t.body}</div>
            <p className="mt-2 text-xs text-muted-foreground">{t.note}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ExamplesSection() {
  const rows = EXAMPLES.map((ex) => ({
    ...ex,
    check: computeCheckDigit(ex.body),
  }));
  return (
    <>
      <h2>Examples</h2>
      <p>
        Verified samples for every supported format. The complete code is the body with the
        calculated check digit appended.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Format</th>
              <th scope="col">Body</th>
              <th scope="col">Check digit</th>
              <th scope="col">Full code</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.body}>
                <td>{r.kind}</td>
                <td>
                  <code>{r.body}</code>
                </td>
                <td>
                  <strong>{r.check}</strong>
                </td>
                <td>
                  <code>
                    {r.body}
                    <strong>{r.check}</strong>
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}
function RelatedTools() {
  const related = [
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify an existing GTIN-8/12/13/14 check digit.",
    },
    {
      path: "/upc-lookup",
      label: "UPC Lookup",
      desc: "Validate UPC-A / UPC-E and decode the number system digit.",
    },
    {
      path: "/ean-lookup",
      label: "EAN Lookup",
      desc: "Validate EAN-13 / EAN-8 and decode the GS1 country prefix.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan a barcode with your camera or upload an image.",
    },
    {
      path: "/read-barcode-from-image",
      label: "Read Barcode From Image",
      desc: "Decode a barcode from a photo or screenshot.",
    },
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Decode QR codes from camera or image.",
    },
    {
      path: "/barcode-guides/check-digits-explained",
      label: "Check Digits Explained",
      desc: "Why mod-10 catches typos and how ISBN-10 uses mod-11.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-semibold group-hover:text-primary">{r.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
